import classNames from 'classnames';
import { createElement, Children, Fragment, PureComponent } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var Loading = (function (color, borderColor) {
    if (color === void 0) { color = '#aaa'; }
    if (borderColor === void 0) { borderColor = 'white'; }
    return function () { return (createElement("div", { className: "m-style-picker-spinner" },
        createElement("div", { className: "rect1", style: { backgroundColor: color, borderColor: borderColor } }),
        createElement("div", { className: "rect2", style: { backgroundColor: color, borderColor: borderColor } }),
        createElement("div", { className: "rect3", style: { backgroundColor: color, borderColor: borderColor } }),
        createElement("div", { className: "rect4", style: { backgroundColor: color, borderColor: borderColor } }),
        createElement("div", { className: "rect5", style: { backgroundColor: color, borderColor: borderColor } }))); };
});

var smooth = function (fun) {
    return function (offset, maxOffset) {
        var floor = Math.floor(offset);
        var ceil = Math.ceil(offset);
        if (floor === ceil) {
            return fun(offset, maxOffset);
        }
        else {
            return fun(floor, maxOffset) * (1 - offset + floor) + fun(ceil, maxOffset) * (offset - floor);
        }
    };
};
var clamp = function (value, min, max) {
    return Math.min(Math.max(value, min), max);
};

var emptySmoothFun = function (fun) { return fun; };
var CLICK_SELECT_MAX_OFFSET = 3;
var PREFIX_CLASS = 'm-style-picker';
var Item = function (ignored) { return null; };
var createVelocity = function (minInterval, maxInterval) {
    if (minInterval === void 0) { minInterval = 30; }
    if (maxInterval === void 0) { maxInterval = 100; }
    var time = 0;
    var y = 0;
    var velocity = 0;
    var recorder = {
        reset: function () {
            time = 0;
            y = 0;
            velocity = 0;
        },
        getVelocity: function (aY) {
            if (aY !== y) {
                recorder.record(aY);
            }
            return velocity;
        },
        record: function (aY) {
            var now = +new Date();
            velocity = (aY - y) / (now - time);
            if (now - time >= minInterval) {
                velocity = now - time <= maxInterval ? velocity : 0;
                y = aY;
                time = now;
            }
        },
    };
    return recorder;
};
var createStep = function (lineHeight, maxInterval) {
    if (maxInterval === void 0) { maxInterval = 600; }
    var time = 0;
    var step = 0;
    return {
        reset: function () {
            time = 0;
            step = 0;
        },
        move: function (delta, mode) {
            var now = +new Date();
            if (now - time > maxInterval) {
                time = now;
                step = 0;
                return 0;
            }
            if (mode === 0) {
                step += delta / lineHeight;
            }
            else {
                step += delta;
            }
            if (step >= 1) {
                time = now;
                step = 0;
                return 1;
            }
            if (step <= -1) {
                time = now;
                step = 0;
                return -1;
            }
            return 0;
        }
    };
};
var setTransform = function (nodeStyle, value) {
    nodeStyle.transform = value;
    // noinspection JSDeprecatedSymbols
    nodeStyle.webkitTransform = value;
    nodeStyle.msTransform = value;
    nodeStyle.MozTransform = value;
    nodeStyle.OTransform = value;
};
var setTransition = function (nodeStyle, value) {
    nodeStyle.transition = value;
    // noinspection JSDeprecatedSymbols
    nodeStyle.webkitTransition = value;
};
var createDefaultIndicator = function (key, size, marginStart, marginEnd, mode, className, style) {
    var finalStyle;
    if (mode === 'vertical') {
        finalStyle = __assign({}, style, { width: '100%', height: size + marginStart + marginEnd });
    }
    else {
        finalStyle = __assign({}, style, { height: '100%', width: size + marginStart + marginEnd });
    }
    return (createElement("div", { key: key, className: PREFIX_CLASS + "-indicator " + PREFIX_CLASS + "-indicator-" + mode + " " + (className || ''), style: finalStyle }));
};
var Picker = /** @class */ (function (_super) {
    __extends(Picker, _super);
    function Picker(props) {
        var _this = _super.call(this, props) || this;
        _this.scrollHandlers = (function () {
            var scroll = -1;
            var last = 0;
            var start = 0;
            var scrollDisabled = false;
            var isMoving = false;
            var scrollTo = function (target, time) {
                if (time === void 0) { time = .3; }
                var _a = _this.props.mode, mode = _a === void 0 ? 'vertical' : _a;
                if (scroll !== target) {
                    scroll = target;
                    if (time && !_this.props.noAnimate) {
                        setTransition(_this.contentRef.style, "cubic-bezier(0,0,0.2,1.15) " + time + "s");
                        _this.items.filter(function (item) { return item; }).forEach(function (item) {
                            setTransition(item.style, "cubic-bezier(0,0,0.2,1.15) " + time + "s");
                        });
                        _this.itemsMargin.filter(function (item) { return item; }).forEach(function (item) {
                            setTransition(item.style, "cubic-bezier(0,0,0.2,1.15) " + time + "s");
                        });
                        setTimeout(function () {
                            _this.scrollingComplete();
                            if (_this.contentRef) {
                                setTransition(_this.contentRef.style, '');
                            }
                            _this.items.filter(function (item) { return item; }).forEach(function (item) {
                                setTransition(item.style, '');
                            });
                            _this.itemsMargin.filter(function (item) { return item; }).forEach(function (item) {
                                setTransition(item.style, '');
                            });
                        }, +time * 1000);
                    }
                    switch (mode) {
                        case "vertical":
                            setTransform(_this.contentRef.style, "translate3d(0," + -target + "px,0)");
                            break;
                        case "horizontal":
                            setTransform(_this.contentRef.style, "translate3d(" + -target + "px,0,0)");
                    }
                }
            };
            var Velocity = createVelocity();
            var Step;
            var onFinish = function () {
                if (!isMoving) {
                    return;
                }
                isMoving = false;
                var target = scroll;
                var time = .3;
                var velocity = Velocity.getVelocity(target) * 4;
                if (velocity) {
                    target = velocity * 40 + target;
                    time = Math.abs(velocity) * .1;
                }
                target = _this.adaptScrollTarget(target);
                scrollTo(target, time < .3 ? .3 : time);
                _this.onScrollChange();
            };
            var onStart = function (y) {
                if (scrollDisabled || !_this.supportDragSelect() || _this.props.loading) {
                    return;
                }
                isMoving = true;
                start = y;
                last = scroll;
            };
            var onMove = function (y) {
                if (!isMoving) {
                    return;
                }
                var _a = _this.props.mode, mode = _a === void 0 ? 'vertical' : _a;
                scroll = last - y + start;
                Velocity.record(scroll);
                _this.onScrollChange();
                switch (mode) {
                    case "vertical":
                        setTransform(_this.contentRef.style, "translate3d(0," + -scroll + "px,0)");
                        break;
                    case "horizontal":
                        setTransform(_this.contentRef.style, "translate3d(" + -scroll + "px,0,0)");
                        break;
                }
            };
            var supportsWheel = false;
            var onWheel = function (evt) {
                if (!_this.supportScrollSelect() || _this.props.loading) {
                    return false;
                }
                if (evt.type === 'wheel') {
                    supportsWheel = true;
                }
                else if (supportsWheel) {
                    return false;
                }
                if (!Step) {
                    Step = createStep(34);
                }
                evt.preventDefault();
                evt.stopPropagation();
                var _a = _this.props.mode, mode = _a === void 0 ? 'vertical' : _a;
                if (mode === 'vertical') {
                    var delta = evt.deltaY;
                    var step = Step.move(delta, evt.deltaMode);
                    if (step === 1) {
                        _this.selectNext(_this.scrollTo);
                    }
                    if (step === -1) {
                        _this.selectLast(_this.scrollTo);
                    }
                }
                return false;
            };
            return {
                touchstart: function (evt) {
                    var _a = _this.props.mode, mode = _a === void 0 ? 'vertical' : _a;
                    onStart(mode === 'vertical' ? evt.touches[0].screenY : evt.touches[0].screenX);
                },
                mousedown: function (evt) {
                    var _a = _this.props.mode, mode = _a === void 0 ? 'vertical' : _a;
                    onStart(mode === 'vertical' ? evt.screenY : evt.screenX);
                },
                touchmove: function (evt) {
                    evt.preventDefault();
                    var _a = _this.props.mode, mode = _a === void 0 ? 'vertical' : _a;
                    onMove(mode === 'vertical' ? evt.touches[0].screenY : evt.touches[0].screenX);
                },
                mousemove: function (evt) {
                    evt.preventDefault();
                    var _a = _this.props.mode, mode = _a === void 0 ? 'vertical' : _a;
                    onMove(mode === 'vertical' ? evt.screenY : evt.screenX);
                },
                touchend: function () { return onFinish(); },
                touchcancel: function () { return onFinish(); },
                mouseup: function () { return onFinish(); },
                wheel: onWheel,
                getValue: function () {
                    return scroll;
                },
                scrollTo: scrollTo,
                setDisabled: function (disabled) {
                    if (disabled === void 0) { disabled = false; }
                    scrollDisabled = disabled;
                },
            };
        })();
        _this.mapChildren = function (item, index) {
            var _a = _this.props, _b = _a.mode, mode = _b === void 0 ? 'vertical' : _b, itemStyle = _a.itemStyle, children = _a.children;
            var itemClassName = PREFIX_CLASS + "-item";
            var itemModeClassName = itemClassName + "-" + mode;
            var selectedItemClassName = itemClassName + " " + PREFIX_CLASS + "-item-selected";
            var itemMarginClassName = PREFIX_CLASS + "-item-margin";
            var itemMarginModeClassName = itemMarginClassName + "-" + mode;
            var numChildren = Children.count(children);
            var _c = item.props, _d = _c.className, className = _d === void 0 ? '' : _d, style = _c.style, value = _c.value, nodeRef = _c.nodeRef, onClick = _c.onClick, onMouseDown = _c.onMouseDown, onMouseUp = _c.onMouseUp, onTouchStart = _c.onTouchStart, onTouchEnd = _c.onTouchEnd, rest = __rest(_c, ["className", "style", "value", "nodeRef", "onClick", "onMouseDown", "onMouseUp", "onTouchStart", "onTouchEnd"]);
            var startX;
            var startY;
            var dist = -1;
            var onMStart = function (evt) {
                startX = evt.clientX;
                startY = evt.clientY;
                if (onMouseDown) {
                    onMouseDown(evt);
                }
            };
            var onMFinish = function (evt) {
                dist = (evt.clientX - startX) * (evt.clientX - startX) + (evt.clientY - startY) * (evt.clientY - startY);
                if (onMouseUp) {
                    onMouseUp(evt);
                }
            };
            var onTStart = function (evt) {
                startX = evt.touches[0].clientX;
                startY = evt.touches[0].clientY;
                if (onTouchStart) {
                    onTouchStart(evt);
                }
            };
            var onTFinish = function (evt) {
                dist = (evt.touches[0].clientX - startX) * (evt.touches[0].clientX - startX) + (evt.touches[0].clientY - startY) * (evt.touches[0].clientY - startY);
                if (onTouchEnd) {
                    onTouchEnd(evt);
                }
            };
            var onClickHandler = function (evt) {
                if (dist < CLICK_SELECT_MAX_OFFSET && onClick) {
                    onClick(evt);
                }
                if (dist < CLICK_SELECT_MAX_OFFSET && !evt.isDefaultPrevented() && _this.supportClickSelect()) {
                    _this.clickToScroll(value);
                }
            };
            var base = _this.calcBaseItemSize();
            var maxOffset = _this.calcMaxOffset();
            var offset = _this.calcOffset(index, _this.scrollHandlers.getValue(), base, maxOffset);
            var itemSize = _this.calcItemSize(offset, base);
            var margin = _this.calcItemMargin(offset, 1);
            var finalStyle;
            var marginStyle;
            if (mode === 'vertical') {
                finalStyle = __assign({}, itemStyle, style, { flexBasis: itemSize, height: itemSize, lineHeight: itemSize + "px", margin: 0 });
                marginStyle = {
                    flexBasis: margin,
                    height: margin,
                };
            }
            else {
                finalStyle = __assign({}, itemStyle, style, { flexBasis: itemSize, width: itemSize, lineHeight: _this.state.rootHeight + "px", margin: 0 });
                marginStyle = {
                    flexBasis: margin,
                    width: margin,
                };
            }
            var bindRef = function (ref) {
                _this.items[index] = ref;
                if (typeof nodeRef === 'function') {
                    nodeRef(ref);
                }
                else if (typeof nodeRef === 'object') {
                    nodeRef.current = ref;
                }
            };
            return (createElement(Fragment, { key: value },
                createElement("div", __assign({ style: finalStyle, className: (value === _this.state.value ? selectedItemClassName : itemClassName) + " " + itemModeClassName + " " + className, ref: bindRef, onMouseDown: onMStart, onMouseUp: onMFinish, onTouchStart: onTStart, onTouchEnd: onTFinish, onClick: onClickHandler }, rest), item.props.children || null),
                index !== numChildren - 1 ? (createElement("div", { className: itemMarginClassName + " " + itemMarginModeClassName, style: marginStyle, ref: function (ref) { return _this.itemsMargin[index] = ref; } })) : null));
        };
        _this.bindRootRef = function (ref) {
            var lastRef = _this.rootRef;
            _this.rootRef = ref;
            if (lastRef !== ref) {
                if (lastRef) {
                    _this.observer.unobserve(lastRef);
                }
                if (ref) {
                    _this.layout(ref.getBoundingClientRect().width, ref.getBoundingClientRect().height);
                    _this.observer.observe(ref);
                }
            }
        };
        _this.calcCenterOffset = function (width, height, mode) {
            if (mode === void 0) { mode = 'vertical'; }
            var base = _this.calcBaseItemSize(width, height);
            var indicatorUpMargin = _this.calcItemMargin(0, -1);
            var indicatorDownMargin = _this.calcItemMargin(0, 1);
            var indicatorSize = _this.calcItemSize(0, base) + indicatorUpMargin + indicatorDownMargin;
            if (mode === 'vertical') {
                return {
                    indicatorUpMargin: indicatorUpMargin,
                    indicatorDownMargin: indicatorDownMargin,
                    indicatorSize: indicatorSize,
                    centerOffset: (height - indicatorSize) / 2,
                };
            }
            else {
                return {
                    indicatorUpMargin: indicatorUpMargin,
                    indicatorDownMargin: indicatorDownMargin,
                    indicatorSize: indicatorSize,
                    centerOffset: (width - indicatorSize) / 2,
                };
            }
        };
        _this.calcMaskStyles = function () {
            var _a = _this.props, _b = _a.mode, mode = _b === void 0 ? 'vertical' : _b, _c = _a.mask, mask = _c === void 0 ? true : _c;
            var maskValue;
            if (typeof mask === 'boolean') {
                maskValue = mask ? [0.1, 0.7] : [0, 0];
            }
            else {
                maskValue = mask;
            }
            if (mode === 'vertical') {
                return [
                    {
                        backgroundImage: "linear-gradient(to bottom, rgba(255, 255, 255, " + maskValue[1] + "), rgba(255, 255, 255, " + maskValue[0] + "))",
                        backgroundPosition: 'top',
                        top: 0,
                        left: 0,
                    },
                    {
                        backgroundImage: "linear-gradient(to top, rgba(255, 255, 255, " + maskValue[1] + "), rgba(255, 255, 255, " + maskValue[0] + "))",
                        backgroundPosition: 'bottom',
                    }
                ];
            }
            else {
                return [
                    {
                        backgroundImage: "linear-gradient(to right, rgba(255, 255, 255, " + maskValue[1] + "), rgba(255, 255, 255, " + maskValue[0] + "))",
                        backgroundPosition: 'left',
                        top: 0,
                        left: 0,
                    },
                    {
                        backgroundImage: "linear-gradient(to left, rgba(255, 255, 255, " + maskValue[1] + "), rgba(255, 255, 255, " + maskValue[0] + "))",
                        backgroundPosition: 'right',
                    },
                ];
            }
        };
        _this.layout = function (width, height) {
            var _a = _this.props.mode, mode = _a === void 0 ? 'vertical' : _a;
            var _b = _this.calcCenterOffset(width, height, mode), centerOffset = _b.centerOffset, indicatorSize = _b.indicatorSize, indicatorUpMargin = _b.indicatorUpMargin;
            if (mode === 'vertical') {
                _this.contentRef.style.paddingTop = centerOffset + indicatorUpMargin + "px";
                _this.contentRef.style.paddingLeft = '0px';
                _this.maskRef1.style.width = '100%';
                _this.maskRef1.style.height = centerOffset + "px";
                _this.maskRef2.style.top = centerOffset + indicatorSize + "px";
                _this.maskRef2.style.left = '0px';
                _this.maskRef2.style.width = '100%';
                _this.maskRef2.style.height = centerOffset + "px";
            }
            else {
                _this.contentRef.style.paddingTop = '0px';
                _this.contentRef.style.paddingLeft = centerOffset + indicatorUpMargin + "px";
                _this.maskRef1.style.height = '100%';
                _this.maskRef1.style.width = centerOffset + "px";
                _this.maskRef2.style.top = '0px';
                _this.maskRef2.style.left = centerOffset + indicatorSize + "px";
                _this.maskRef2.style.height = '100%';
                _this.maskRef2.style.width = centerOffset + "px";
            }
            _this.createGridsOffset();
            var state = {
                rootWidth: width,
                rootHeight: height,
                centerOffset: centerOffset,
            };
            _this.setState(state);
            return state;
        };
        _this.getGridRef = function (offset, maxOffset) {
            maxOffset = maxOffset === undefined ? _this.calcMaxOffset() : maxOffset;
            return _this.gridsRef[offset + maxOffset];
        };
        _this.createGridsOffset = function (maxOffset) {
            maxOffset = maxOffset === undefined ? _this.calcMaxOffset() : maxOffset;
            var _a = _this.props.mode, mode = _a === void 0 ? 'vertical' : _a;
            var centerGridRef = _this.getGridRef(0, maxOffset);
            for (var offset = -maxOffset; offset <= maxOffset; ++offset) {
                var gridRef = _this.getGridRef(offset, maxOffset);
                if (centerGridRef && gridRef) {
                    if (mode === 'vertical') {
                        _this.gridsOffset[offset + maxOffset] = gridRef.getBoundingClientRect().top - centerGridRef.getBoundingClientRect().top;
                    }
                    else {
                        _this.gridsOffset[offset + maxOffset] = gridRef.getBoundingClientRect().left - centerGridRef.getBoundingClientRect().left;
                    }
                }
                else {
                    _this.gridsOffset[offset + maxOffset] = 0;
                }
            }
        };
        _this.getGridOffset = function (offset, maxOffset) {
            maxOffset = maxOffset === undefined ? _this.calcMaxOffset() : maxOffset;
            return _this.gridsOffset[offset + maxOffset] || 0;
        };
        _this.createGrids = function () {
            var maxOffset = _this.calcMaxOffset();
            var nodes = [];
            var _loop_1 = function (i) {
                var size = _this.calcItemSize(i);
                var margin = _this.calcItemMargin(i, 1);
                var style = {
                    flexGrow: size,
                    flexShrink: size,
                };
                var bindRef = i !== 0
                    ? (function (ref) { return _this.gridsRef[i + maxOffset] = ref; })
                    : (function (ref) {
                        var lastRef = _this.gridsRef[i + maxOffset];
                        _this.gridsRef[i + maxOffset] = ref;
                        if (lastRef !== ref) {
                            if (lastRef) {
                                _this.observer.unobserve(lastRef);
                            }
                            if (ref) {
                                if (_this.rootRef) {
                                    _this.layout(_this.rootRef.getBoundingClientRect().width, _this.rootRef.getBoundingClientRect().height);
                                }
                                _this.observer.observe(ref);
                            }
                        }
                    });
                nodes.push(createElement("div", { key: i, className: "m-style-picker-grid", style: style, ref: bindRef }));
                if (i !== maxOffset) {
                    var marginStyle = {
                        flexGrow: 0,
                        flexShrink: 0,
                        flexBasis: margin,
                    };
                    nodes.push(createElement("div", { key: "m-" + (i < 0 ? 'n' + i : i) + "-" + (i + 1 < 0 ? 'n' + (i + 1) : i + 1), style: marginStyle }));
                }
            };
            for (var i = -maxOffset; i <= maxOffset; ++i) {
                _loop_1(i);
            }
            return nodes;
        };
        _this.supportDragSelect = function () {
            var _a = _this.props.dragSelect, dragSelect = _a === void 0 ? true : _a;
            return dragSelect;
        };
        _this.supportClickSelect = function () {
            var _a = _this.props.clickSelect, clickSelect = _a === void 0 ? true : _a;
            return clickSelect;
        };
        _this.supportScrollSelect = function () {
            var _a = _this.props.scrollSelect, scrollSelect = _a === void 0 ? true : _a;
            return scrollSelect;
        };
        _this.normedSize = function () {
            var _a = _this.props.size, size = _a === void 0 ? 7 : _a;
            if (size <= 0) {
                size = 1;
            }
            if (size % 2 !== 1) {
                size += 1;
            }
            return size;
        };
        _this.resolveDecoratorFun = function () {
            var _a = _this.props.smooth, smooth$1 = _a === void 0 ? true : _a;
            if (typeof smooth$1 === 'boolean') {
                return smooth$1 ? smooth : emptySmoothFun;
            }
            else {
                return smooth$1;
            }
        };
        _this.calcItemWeight = function (offset, maxOffset) {
            if (maxOffset === undefined) {
                maxOffset = _this.calcMaxOffset();
            }
            var _a = _this.props, itemSize = _a.itemSize, _b = _a.itemWeight, itemWeight = _b === void 0 ? 1 : _b;
            if (typeof itemSize === 'number') {
                return itemSize;
            }
            else if (typeof itemSize === 'function') {
                var decorator = _this.resolveDecoratorFun();
                var itemSizeFun = decorator(itemSize);
                if (offset > maxOffset) {
                    return itemSizeFun(maxOffset, maxOffset);
                }
                else if (offset > -maxOffset) {
                    var ceil = Math.ceil(offset);
                    var floor = Math.floor(offset);
                    return ceil === floor ? itemSizeFun(offset, maxOffset) : (itemSizeFun(floor, maxOffset) * (1 - offset + floor) + itemSizeFun(ceil, maxOffset) * (offset - floor));
                }
                else {
                    return itemSizeFun(-maxOffset, maxOffset);
                }
            }
            else if (typeof itemWeight === 'number') {
                return itemWeight;
            }
            else if (typeof itemWeight === 'function') {
                var decorator = _this.resolveDecoratorFun();
                var itemWeightFun = decorator(itemWeight);
                if (offset > maxOffset) {
                    return itemWeightFun(maxOffset, maxOffset);
                }
                else if (offset > -maxOffset) {
                    var ceil = Math.ceil(offset);
                    var floor = Math.floor(offset);
                    return ceil === floor ? itemWeightFun(offset, maxOffset) : (itemWeightFun(floor, maxOffset) * (1 - offset + floor) + itemWeightFun(ceil, maxOffset) * (offset - floor));
                }
                else {
                    return itemWeightFun(-maxOffset, maxOffset);
                }
            }
            else {
                return 1;
            }
        };
        _this.calcBaseItemSize = function (rootWidth, rootHeight) {
            var cSize;
            var _a = _this.props.mode, mode = _a === void 0 ? 'vertical' : _a;
            if (mode === 'vertical') {
                cSize = rootHeight === undefined ? _this.state.rootHeight : rootHeight;
            }
            else {
                cSize = rootWidth === undefined ? _this.state.rootWidth : rootWidth;
            }
            var multi = 0;
            var maxOffset = _this.calcMaxOffset();
            for (var i = -maxOffset; i <= maxOffset; ++i) {
                var weight = _this.calcItemWeight(i, maxOffset);
                multi += weight;
                if (i !== maxOffset) {
                    cSize -= _this.calcItemMargin(i, 1);
                }
            }
            if (cSize <= 0) {
                return 0;
            }
            return Math.max(cSize / multi, 0);
        };
        _this.calcItemSize = function (offset, base) {
            if (base === undefined) {
                base = _this.calcBaseItemSize();
            }
            var weight = _this.calcItemWeight(offset);
            return weight * base;
        };
        _this.calcItemMargin = function (offset, dir) {
            var _a = _this.props.itemMargin, itemMargin = _a === void 0 ? 0 : _a;
            var maxOffset = _this.calcMaxOffset();
            if (offset <= -maxOffset - 1 || offset >= maxOffset + 1) {
                return 0;
            }
            else if (offset <= -maxOffset && dir < 0) {
                return 0;
            }
            else if (offset >= maxOffset && dir > 0) {
                return 0;
            }
            if (typeof itemMargin === 'number') {
                return itemMargin;
            }
            else {
                var decorator = _this.resolveDecoratorFun();
                var marginFun = decorator(itemMargin);
                var margin = marginFun(offset, maxOffset);
                if (dir === 0) {
                    return margin;
                }
                var adjMargin = void 0;
                if (dir < 0) {
                    adjMargin = marginFun(offset - 1, maxOffset);
                }
                else {
                    adjMargin = marginFun(offset + 1, maxOffset);
                }
                if (margin >= 0 && adjMargin >= 0) {
                    return Math.max(margin, adjMargin);
                }
                else if (margin <= 0 && adjMargin <= 0) {
                    return Math.min(margin, adjMargin);
                }
                else {
                    return margin + adjMargin;
                }
            }
        };
        _this.calcMaxOffset = function () {
            return (_this.normedSize() - 1) / 2;
        };
        _this.calcOffset = function (index, scroll, base, maxOffset) {
            base = base === undefined ? _this.calcBaseItemSize() : base;
            maxOffset = maxOffset === undefined ? _this.calcMaxOffset() : maxOffset;
            scroll = scroll === undefined ? _this.scrollHandlers.getValue() : scroll;
            var max = -_this.getGridOffset(-maxOffset, maxOffset);
            var topItemSize = _this.calcItemSize(-maxOffset, base);
            if (scroll >= max) {
                return index - maxOffset - clamp((scroll - max) / topItemSize, 0, Children.count(_this.props.children) - maxOffset - 1);
            }
            else if (scroll >= 0) {
                for (var i = -1; i >= -maxOffset; --i) {
                    var offset = -_this.getGridOffset(i, maxOffset);
                    if (offset > scroll) {
                        return index + i + (offset - scroll) / (_this.calcItemSize(i, base) + _this.calcItemMargin(i, 1));
                    }
                }
                throw new Error('This is impossible!');
            }
            else {
                return index;
            }
        };
        _this.calcScroll = function (offset, base, maxOffset) {
            base = base === undefined ? _this.calcBaseItemSize() : base;
            maxOffset = maxOffset === undefined ? _this.calcMaxOffset() : maxOffset;
            var bottomBoundOffset = _this.getGridOffset(maxOffset, maxOffset);
            var upBoundOffset = _this.getGridOffset(-maxOffset, maxOffset);
            if (offset > maxOffset) {
                return -bottomBoundOffset + (maxOffset - offset) * _this.calcItemSize(maxOffset, base);
            }
            else if (offset < -maxOffset) {
                return -upBoundOffset - (maxOffset + offset) * _this.calcItemSize(-maxOffset, base);
            }
            else {
                return -_this.getGridOffset(offset, maxOffset);
            }
        };
        _this.calcCurrentIndexByScroll = function (scroll, base, maxOffset) {
            var offset = _this.calcOffset(0, scroll, base, maxOffset);
            return Math.max(Math.round(-offset), 0);
        };
        _this.adaptScrollTarget = function (target) {
            if (target <= 0) {
                return 0;
            }
            var maxOffset = _this.calcMaxOffset();
            var base = _this.calcBaseItemSize();
            var offset = _this.calcOffset(0, target, base, maxOffset);
            return _this.calcScroll(Math.round(offset), base, maxOffset);
        };
        _this.calcContainerSizeByProps = function () {
            var _a = _this.props.itemSize, itemSize = _a === void 0 ? 34 : _a;
            var offset = _this.calcMaxOffset();
            var cSize = 0;
            for (var i = -offset; i <= offset; ++i) {
                var size = void 0;
                if (typeof itemSize === 'number') {
                    size = itemSize;
                }
                else {
                    var decorator = _this.resolveDecoratorFun();
                    size = decorator(itemSize)(i, offset);
                }
                cSize += size;
                if (i !== offset) {
                    cSize += _this.calcItemMargin(i, 1);
                }
            }
            return cSize;
        };
        _this.calcIndex = function (value) {
            var children = Children.toArray(_this.props.children);
            for (var i = 0, len = children.length; i < len; ++i) {
                if (children[i].props.value === value) {
                    return i;
                }
            }
            return -1;
        };
        _this.select = function (value, scrollTo) {
            var index = _this.calcIndex(value);
            _this.selectByIndex(index !== -1 ? index : 0, scrollTo);
        };
        _this.selectByIndex = function (index, scrollTo) {
            if (index < 0 || index >= Children.count(_this.props.children)) {
                return;
            }
            var scroll = _this.calcScroll(-index);
            scrollTo(scroll);
        };
        _this.selectLast = function (scrollTo) {
            var index = _this.calcIndex(_this.state.value);
            _this.selectByIndex(index !== -1 ? index - 1 : 0, scrollTo);
        };
        _this.selectNext = function (scrollTo) {
            var index = _this.calcIndex(_this.state.value);
            _this.selectByIndex(index !== -1 ? index + 1 : 0, scrollTo);
        };
        _this.doScrollingComplete = function (top, fireValueChange) {
            var children = Children.toArray(_this.props.children);
            var index = _this.calcCurrentIndexByScroll(top);
            var child = children[index];
            if (child) {
                fireValueChange(child.props.value);
                // tslint:disable-next-line:no-console
            }
            else if (console.warn) {
                // tslint:disable-next-line:no-console
                console.warn('child not found', children, index);
            }
        };
        _this.scrollTo = function (top) {
            _this.scrollHandlers.scrollTo(top);
            _this.setScrollEffect();
        };
        _this.scrollToWithoutAnimation = function (top) {
            _this.scrollHandlers.scrollTo(top, 0);
            _this.setScrollEffect();
        };
        _this.fireValueChange = function (value) {
            if (value !== _this.state.value) {
                if (!('value' in _this.props)) {
                    _this.setState({
                        value: value,
                    });
                }
                if (_this.props.onChange) {
                    _this.props.onChange(value);
                }
            }
        };
        _this.onScrollChange = function () {
            var top = _this.scrollHandlers.getValue();
            if (top >= 0) {
                _this.setScrollEffect();
                var children = Children.toArray(_this.props.children);
                var index = _this.calcCurrentIndexByScroll(top);
                if (_this.scrollValue !== index) {
                    _this.scrollValue = index;
                    var child = children[index];
                    if (child && _this.props.onScrollChange) {
                        _this.props.onScrollChange(child.props.value);
                        // tslint:disable-next-line:no-console
                    }
                    else if (!child && console.warn) {
                        // tslint:disable-next-line:no-console
                        console.warn('child not found', children, index);
                    }
                }
            }
        };
        _this.scrollingComplete = function () {
            var top = _this.scrollHandlers.getValue();
            if (top >= 0) {
                _this.doScrollingComplete(top, _this.fireValueChange);
            }
        };
        // react渲染会有性能问题，故在这里使用传统方法
        _this.setScrollEffect = function () {
            var _a = _this.props.mode, mode = _a === void 0 ? 'vertical' : _a;
            var _b = _this.props.rotate, rotate = _b === void 0 ? false : _b;
            var maxOffset = _this.calcMaxOffset();
            if (rotate === true) {
                rotate = maxOffset && (90 / (maxOffset + 1));
            }
            else if (typeof rotate !== 'number') {
                rotate = 0;
            }
            var angle = (rotate / 180) * Math.PI;
            _this.items.forEach(function (item, index) {
                if (!item) {
                    return;
                }
                var offset = _this.calcOffset(index);
                var itemSize = _this.calcItemSize(offset);
                item.style.flexBasis = itemSize + "px";
                if (mode === 'vertical') {
                    item.style.height = itemSize + "px";
                    item.style.lineHeight = itemSize + "px";
                    if (rotate !== 0 && offset !== 0) {
                        var offsetIndex = clamp(offset, -90 / rotate, 90 / rotate);
                        item.style.transform = "translateY(" + itemSize * (offsetIndex - Math.sin(offsetIndex * angle) / angle) + "px) rotateX(" + -offsetIndex * rotate + "deg)";
                    }
                    else {
                        item.style.transform = '';
                    }
                }
                else {
                    item.style.width = itemSize + "px";
                    if (rotate !== 0 && offset !== 0) {
                        var offsetIndex = clamp(offset, -90 / rotate, 90 / rotate);
                        item.style.transform = "translateX(" + itemSize * (offsetIndex - Math.sin(offsetIndex * angle) / angle) + "px) rotateY(" + -offsetIndex * rotate + "deg)";
                    }
                    else {
                        item.style.transform = '';
                    }
                }
            });
            _this.itemsMargin.forEach(function (item, index) {
                if (!item) {
                    return;
                }
                var offset = _this.calcOffset(index);
                var itemMargin = _this.calcItemMargin(offset, 1);
                item.style.flexBasis = itemMargin + "px";
                if (mode === 'vertical') {
                    item.style.height = itemMargin + "px";
                }
                else {
                    item.style.width = itemMargin + "px";
                }
            });
        };
        _this.clickToScroll = function (value) {
            _this.select(value, _this.scrollTo);
        };
        var selectedValueState;
        var _a = _this.props, value = _a.value, defaultValue = _a.defaultValue;
        if (value !== undefined) {
            selectedValueState = value;
        }
        else if (defaultValue !== undefined) {
            selectedValueState = defaultValue;
        }
        else {
            var children = Children.toArray(_this.props.children);
            selectedValueState = children && children[0] && children[0].props.value;
        }
        _this.state = {
            value: selectedValueState,
            rootWidth: 0,
            rootHeight: 0,
        };
        _this.items = [];
        _this.itemsMargin = [];
        _this.gridsRef = [];
        _this.gridsOffset = [];
        _this.observer = new ResizeObserver(function () {
            _this.layout(_this.rootRef.getBoundingClientRect().width, _this.rootRef.getBoundingClientRect().height);
        });
        return _this;
    }
    Picker.prototype.componentDidMount = function () {
        var rootRef = this.rootRef;
        this.scrollHandlers.setDisabled(this.props.disabled);
        this.select(this.state.value, this.scrollTo);
        var passiveSupported = this.passiveSupported();
        var willPreventDefault = passiveSupported ? { passive: false } : false;
        var willNotPreventDefault = passiveSupported ? { passive: true } : false;
        rootRef.addEventListener('touchstart', this.scrollHandlers.touchstart, willNotPreventDefault);
        document.addEventListener('touchcancel', this.scrollHandlers.touchcancel, willNotPreventDefault);
        document.addEventListener('touchend', this.scrollHandlers.touchend, willNotPreventDefault);
        document.addEventListener('touchmove', this.scrollHandlers.touchmove, willPreventDefault);
        rootRef.addEventListener('mousedown', this.scrollHandlers.mousedown, willNotPreventDefault);
        document.addEventListener('mousemove', this.scrollHandlers.mousemove, willPreventDefault);
        document.addEventListener('mouseup', this.scrollHandlers.mouseup, willNotPreventDefault);
        rootRef.addEventListener('wheel', this.scrollHandlers.wheel, willPreventDefault);
        rootRef.addEventListener('mousewheel', this.scrollHandlers.wheel, willPreventDefault);
        rootRef.addEventListener('DOMMouseScroll', this.scrollHandlers.wheel, willPreventDefault);
    };
    Picker.prototype.componentWillUnmount = function () {
        var rootRef = this.rootRef;
        rootRef.removeEventListener('touchstart', this.scrollHandlers.touchstart);
        document.removeEventListener('touchcancel', this.scrollHandlers.touchcancel);
        document.removeEventListener('touchend', this.scrollHandlers.touchend);
        document.removeEventListener('touchmove', this.scrollHandlers.touchmove);
        rootRef.removeEventListener('mousedown', this.scrollHandlers.mousedown);
        document.removeEventListener('mousemove', this.scrollHandlers.mousemove);
        document.removeEventListener('mouseup', this.scrollHandlers.mouseup);
        rootRef.removeEventListener('wheel', this.scrollHandlers.wheel);
        rootRef.removeEventListener('mousewheel', this.scrollHandlers.wheel);
        rootRef.removeEventListener('DOMMouseScroll', this.scrollHandlers.wheel);
        this.observer.disconnect();
    };
    Picker.prototype.componentWillReceiveProps = function (nextProps) {
        var _this = this;
        if ("value" in nextProps && this.state.value !== nextProps.value) {
            this.setState({
                value: nextProps.value,
            }, function () {
                _this.select(nextProps.value, nextProps.noAnimate ? _this.scrollToWithoutAnimation : _this.scrollTo);
            });
        }
        this.scrollHandlers.setDisabled(nextProps.disabled);
    };
    Picker.prototype.componentDidUpdate = function () {
        this.select(this.state.value, this.scrollToWithoutAnimation);
    };
    Picker.prototype.render = function () {
        var _a;
        var _this = this;
        var props = this.props;
        var _b = props.mode, mode = _b === void 0 ? 'vertical' : _b, indicatorClassName = props.indicatorClassName, indicatorStyle = props.indicatorStyle, _c = props.indicatorComponent, indicatorComponent = _c === void 0 ? createDefaultIndicator : _c, children = props.children, _d = props.loading, loading = _d === void 0 ? false : _d, _e = props.loadingComponent, loadingComponent = _e === void 0 ? Loading() : _e;
        // compatibility for preact
        var items = Children ? Children.map(children, this.mapChildren) : [].concat(children).map(this.mapChildren);
        var pickerCls = (_a = {},
            _a[props.className] = !!props.className,
            _a[PREFIX_CLASS] = true,
            _a);
        var pickerStyle;
        var maskStyles = this.calcMaskStyles();
        if (mode === 'vertical') {
            pickerStyle = __assign({}, props.style);
            if (!pickerStyle.height) {
                pickerStyle.height = this.calcContainerSizeByProps();
            }
        }
        else {
            pickerStyle = __assign({}, props.style);
            if (!pickerStyle.width && this.props.itemSize) {
                pickerStyle.width = this.calcContainerSizeByProps();
            }
        }
        if (loading) {
            return (createElement("div", { className: classNames(pickerCls), ref: this.bindRootRef, style: pickerStyle }, loadingComponent() || null));
        }
        else {
            return (createElement("div", { className: classNames(pickerCls), ref: this.bindRootRef, style: pickerStyle },
                createElement("div", { key: "mask1", className: PREFIX_CLASS + "-mask", style: maskStyles[0], ref: function (el) { return _this.maskRef1 = el; } }),
                createElement("div", { key: "mask2", className: PREFIX_CLASS + "-mask", style: maskStyles[1], ref: function (el) { return _this.maskRef2 = el; } }),
                indicatorComponent('indicator', this.calcItemSize(0), this.calcItemMargin(0, -1), this.calcItemMargin(0, 1), mode, indicatorClassName, indicatorStyle),
                createElement("div", { key: "content", className: PREFIX_CLASS + "-content " + PREFIX_CLASS + "-content-" + mode, ref: function (el) { return _this.contentRef = el; } }, items),
                createElement("div", { key: "grids", className: PREFIX_CLASS + "-grids-" + mode }, this.createGrids())));
        }
    };
    Picker.prototype.passiveSupported = function () {
        var passiveSupported = false;
        try {
            var options = Object.defineProperty({}, 'passive', {
                get: function () {
                    passiveSupported = true;
                },
            });
            window.addEventListener('test', null, options);
            // tslint:disable-next-line:no-empty
        }
        catch (err) { }
        return passiveSupported;
    };
    Picker.Item = Item;
    return Picker;
}(PureComponent));

export { Picker, smooth };
